import { useCallback, useEffect, useState } from "react";

import { fetchSheetsHealth } from "./sheets-health-api";
import type { SheetsHealth } from "./sheets-health";
import { useAuth } from "./useAuth";

type UseSheetsHealth = {
  health: SheetsHealth | null;
  /** Replace the state after the owner answers a notice. */
  setHealth: (health: SheetsHealth | null) => void;
  /** Re-ask the API, for the moment a read has just failed. */
  refresh: () => void;
};

/**
 * MOSA-409. The health of one Sheets panel.
 *
 * Read when the panel opens, and again whenever `errorEpoch` moves — the panel
 * bumps that after a failed read, which is the moment a revoked grant or a
 * trashed file shows up. The API does the rest: the check is also what keeps a
 * member's stored state fresh, and its hourly sweep finds the panels nobody
 * opened.
 *
 * `enabled` is false for a Postgres panel and for the shared demo, which have
 * no Google connection to lose.
 */
export function useSheetsHealth({
  datasourceId,
  enabled,
  errorEpoch = 0,
}: {
  datasourceId?: string;
  enabled: boolean;
  errorEpoch?: number;
}): UseSheetsHealth {
  const token = useAuth((state) => state.token);
  const [health, setHealth] = useState<SheetsHealth | null>(null);
  const [epoch, setEpoch] = useState(0);

  const refresh = useCallback(() => setEpoch((value) => value + 1), []);

  useEffect(() => {
    if (!enabled || !datasourceId) {
      setHealth(null);
      return;
    }
    let live = true;
    void fetchSheetsHealth(token, datasourceId).then((result) => {
      if (live) setHealth(result);
    });
    return () => {
      live = false;
    };
  }, [datasourceId, enabled, errorEpoch, epoch, token]);

  return { health, setHealth, refresh };
}
